import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageSquare, Sparkles, Lock, CheckCircle2, Clock } from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TOPICS = ["Behavior", "Puppy", "Service Dog", "Working Dog", "Business", "Life & Purpose"];

export default function FounderQA() {
  const { user, loading } = useAuth();
  const [question, setQuestion] = useState("");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [publicQs, setPublicQs] = useState([]);

  useEffect(() => {
    api.get("/founder-qa/public").then((r) => setPublicQs(r.data.questions || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/founder-qa/me").then((r) => setMine(r.data.questions || [])).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (question.trim().length < 8) { toast.error("Please write a full question."); return; }
    setBusy(true);
    try {
      const r = await api.post("/founder-qa", { question, topic, is_public: isPublic });
      toast.success("Question sent to the Founder's desk.");
      setQuestion("");
      setMine((prev) => [{
        question_id: r.data.question_id,
        question, topic, is_public: isPublic,
        status: "queued", answer: null,
        created_at: new Date().toISOString(),
      }, ...prev]);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Please sign in to ask the founder.");
      else toast.error("Could not send your question.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Section testid="founder-qa-hero" className="pt-40 pb-16">
        <Container>
          <div className="flex items-center gap-3">
            <Sparkles className="text-[color:var(--wk-gold)]" />
            <Eyebrow>Wolfkeeper Plus · Members Only</Eyebrow>
          </div>
          <DisplayH1 className="mt-6 max-w-4xl">Ask the Founder.</DisplayH1>
          <p className="mt-8 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            A private line to Toriano "The Wolfkeeper" Sanzone. Ask about behavior, training,
            business, or life. Toriano personally answers a curated set each month — some
            publicly on Wolfkeeper Plus livestreams, some just for you.
          </p>
        </Container>
      </Section>

      <Section testid="founder-qa-body" className="pb-24">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Form column */}
            <div className="lg:col-span-7">
              {loading ? (
                <div className="text-[color:var(--wk-bone-dim)]">Loading…</div>
              ) : !user ? (
                <div className="card-lux p-10">
                  <Lock className="text-[color:var(--wk-gold)]" />
                  <div className="mt-6 font-serif-display text-3xl">Members-only channel</div>
                  <p className="mt-3 text-[color:var(--wk-bone-dim)] leading-relaxed">
                    Sign in to ask Toriano a question. Wolfkeeper Plus members' questions are
                    prioritized in each month's founder livestream.
                  </p>
                  <div className="mt-6 flex gap-3">
                    <Link to="/login" className="btn-gold">Sign in to ask</Link>
                    <Link to="/membership" className="btn-ghost">Join Wolfkeeper Plus</Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="founder-qa-form" className="card-lux p-8">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="text-[color:var(--wk-gold)]" />
                    <div className="font-serif-display text-2xl">Your question</div>
                  </div>
                  <div className="mt-6">
                    <label className="overline">Topic</label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {TOPICS.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setTopic(t)}
                          data-testid={`qa-topic-${t}`}
                          className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                            topic === t
                              ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]"
                              : "border-white/20 text-[color:var(--wk-bone-dim)] hover:border-[color:var(--wk-gold)]"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6">
                    <label className="overline">Question</label>
                    <textarea
                      required
                      rows={5}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="Ask Toriano anything…"
                      data-testid="qa-question"
                      className="mt-2 w-full bg-transparent border border-white/10 focus:border-[color:var(--wk-gold)] outline-none py-3 px-4 rounded-xl text-base resize-none"
                    />
                    <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)] text-right">{question.length}/1200</div>
                  </div>
                  <label className="mt-4 flex items-center gap-3 text-sm text-[color:var(--wk-bone-dim)] cursor-pointer">
                    <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-[color:var(--wk-gold)]" data-testid="qa-public" />
                    Allow Toriano to answer this publicly (name shown, anonymized on request).
                  </label>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button disabled={busy} className="btn-gold" data-testid="qa-submit">
                      {busy ? "Sending…" : "Send to the Founder"}
                    </button>
                    <div className="text-xs text-[color:var(--wk-bone-dim)] self-center">
                      Response window: typically 7–14 days.
                    </div>
                  </div>
                </form>
              )}

              {/* Mine */}
              {user && mine.length > 0 && (
                <div className="mt-10">
                  <Eyebrow>Your questions</Eyebrow>
                  <div className="mt-6 space-y-4">
                    {mine.map((q) => (
                      <div key={q.question_id} className="card-lux p-6">
                        <div className="flex items-center justify-between gap-3">
                          <div className="overline text-[color:var(--wk-gold)]">{q.topic || "General"}</div>
                          <div className="flex items-center gap-2 text-xs text-[color:var(--wk-bone-dim)]">
                            {q.status === "answered" ? <CheckCircle2 size={14} className="text-[color:var(--wk-gold)]" /> : <Clock size={14} />}
                            {q.status}
                          </div>
                        </div>
                        <div className="mt-3 font-serif-display text-xl leading-tight">"{q.question}"</div>
                        {q.answer && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="overline text-[10px]">Toriano · {q.answered_at?.slice(0,10)}</div>
                            <p className="mt-2 text-[color:var(--wk-bone)] leading-relaxed whitespace-pre-wrap">{q.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Public answered questions */}
            <aside className="lg:col-span-5">
              <div className="sticky top-28">
                <div className="flex items-center justify-between">
                  <div>
                    <Eyebrow>From the Founder's desk</Eyebrow>
                    <div className="mt-2 font-serif-display text-2xl">Recently answered publicly</div>
                  </div>
                  <Link to="/wall" data-testid="qa-view-wall" className="text-sm text-[color:var(--wk-gold)] link-underline shrink-0">View wall →</Link>
                </div>
                <div className="mt-6 space-y-4">
                  {publicQs.length === 0 && (
                    <div className="card-lux p-6 text-sm text-[color:var(--wk-bone-dim)]">
                      Public answers publish here after each monthly founder livestream.
                    </div>
                  )}
                  {publicQs.map((q) => (
                    <motion.div key={q.question_id} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card-lux p-6">
                      <div className="overline text-[color:var(--wk-gold)]">{q.topic}</div>
                      <div className="mt-2 font-serif-display text-lg leading-tight">"{q.question}"</div>
                      <div className="mt-3 text-sm text-[color:var(--wk-bone-dim)]">— {q.user_name}</div>
                      {q.answer && <p className="mt-3 pt-3 border-t border-white/10 text-sm text-[color:var(--wk-bone)] leading-relaxed">{q.answer}</p>}
                    </motion.div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </Section>
    </>
  );
}
